import { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import { api } from '../api';

export interface Closure {
  id: string;
  restaurantId: string;
  startDate: string;
  endDate: string;
  firstReservationTime?: string | null;
  lastReservationTime?: string | null;
  guestFacingReason?: string | null;
}

export const getClosures = async (
  restaurantId: string,
  date: string,
): Promise<Closure[]> =>
  api.get<Closure[]>(
    `/v2/restaurants/${restaurantId}/closures?startDate=${date}&endDate=${date}`,
  );

export const isRestaurantClosed = async (
  restaurantId: string,
  date: string,
): Promise<boolean> => {
  const closures = await getClosures(restaurantId, date);

  return !!closures.length;
};

export const cancel = async (
  restaurantId: string,
  reservationId: string,
): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/reservations/${reservationId}/cancel`);

export const collectCancellationFee = async (
  restaurantId: string,
  reservationId: string,
): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/collect-fee`,
  );

export const waiveCancellationFee = async (
  restaurantId: string,
  reservationId: string,
): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/waive-fee`,
  );

export const refund = async (
  restaurantId: string,
  reservationId: string,
  refundReason: string,
): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/refund`,
    {
      refundReason,
    },
  );

export const updateStaffNote = async ({
  restaurantId,
  reservationId,
  staffNote,
}: {
  restaurantId: string;
  reservationId: string;
  staffNote: string | null;
}): Promise<void> =>
  api.put(
    `/restaurants/${restaurantId}/reservations/${reservationId}/staff-note`,
    { staffNote },
  );

export interface UpcomingReservation {
  date: string;
}

export const getUpcomingReservationsByListing = async (
  restaurantId: string,
  listingId: string,
  signal?: AbortSignal,
): Promise<UpcomingReservation[]> =>
  api.get(`/restaurants/${restaurantId}/listings/${listingId}/reservations`, {
    signal,
  });

export const seatReservation = async ({
  restaurantId,
  reservationId,
  floorPlanTableIds,
}: {
  restaurantId: string;
  reservationId: string;
  floorPlanTableIds: string[];
}): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/reservations/seat`, {
    reservationId,
    floorPlanTableIds,
  });

export const noShow = async ({
  restaurantId,
  reservationId,
}: {
  restaurantId: string;
  reservationId: string;
}): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/no-show`,
  );

export const reactivateReservation = async ({
  reservationId,
  restaurantId,
}: {
  reservationId: string;
  restaurantId: string;
}): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/reservations/${reservationId}/reactivate`,
  );

export const changeReservationSeat = async ({
  restaurantId,
  reservationId,
  floorPlanTableIds,
}: {
  restaurantId: string;
  reservationId: string;
  floorPlanTableIds: string[];
}): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/reservations/seat`, {
    reservationId,
    floorPlanTableIds,
  });

export const changeWalkInSeat = async ({
  restaurantId,
  walkInId,
  floorPlanTableIds,
  turnTime,
  hasAvailability,
  ignoreAvailability,
}: {
  restaurantId: string;
  walkInId: string;
  floorPlanTableIds: HostFloorPlanTable['id'][];
  turnTime: number | null;
  hasAvailability: boolean;
  ignoreAvailability: boolean;
}): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/walk-ins/seat`, {
    walkInId,
    floorPlanTableIds,
    ignoreAvailability,
    turnTime,
    hasAvailability,
  });

export const finishReservation = async ({
  restaurantId,
  reservationId,
}: {
  restaurantId: string;
  reservationId: string;
}): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/reservations/${reservationId}/finish`);

export const unseatReservation = async ({
  restaurantId,
  reservationId,
}: {
  restaurantId: string;
  reservationId: string;
}): Promise<void> =>
  api.delete(`/restaurants/${restaurantId}/reservations/${reservationId}/seat`);

export type OccupantType = 'reservation' | 'walkIn';

export interface GuestAttributes {
  email?: string;
  firstName: string;
  lastName: string;
  phone?: string;
}
export interface ReservationAttributes {
  guest: GuestAttributes;
  guestCount: number;
  guestNotes?: string;
  guestTags: string[];
  hostBookedBy?: string;
  isStranded: boolean;
  listingName: string;
  staffNote?: string;
  time: string;
  date: string;
}

export interface RestaurantReservationsReportResponse {
  name: string;
  reservations: ReservationAttributes[];
}

export const seatWalkIn = async (args: {
  firstName?: string;
  floorPlanTableIds: HostFloorPlanTable['id'][];
  guestCount: number;
  guestId?: string;
  hasAvailability: boolean;
  lastName?: string;
  restaurantId: string;
  saveAsGuest: boolean;
  serverId: string | null;
  turnTime: number | null;
  ignoreAvailability: boolean;
}): Promise<void> => {
  const { restaurantId, ...payload } = args;

  return api.post(`/restaurants/${restaurantId}/walk-ins/seat`, payload);
};

export const seatWaitListEntry = async ({
  floorPlanTableIds,
  restaurantId,
  waitListEntryId,
}: {
  floorPlanTableIds: string[];
  restaurantId: string;
  waitListEntryId: string;
}): Promise<void> =>
  api.post(
    `/restaurants/${restaurantId}/wait-list-entries/${waitListEntryId}/seat`,
    {
      floorPlanTableIds,
      // TODO: hasAvailability and turnTime are deprecated
      hasAvailability: true,
      turnTime: null,
      ignoreAvailability: true,
    },
  );

export const discardWalkIn = async ({
  restaurantId,
  walkInId,
}: {
  restaurantId: string;
  walkInId: string;
}): Promise<void> =>
  api.delete(`/restaurants/${restaurantId}/walk-ins/${walkInId}`);

export const finishWalkIn = async ({
  restaurantId,
  walkInId,
}: {
  restaurantId: string;
  walkInId: string;
}): Promise<void> =>
  api.post(`/restaurants/${restaurantId}/walk-ins/${walkInId}/finish`);

export interface WalkInAvailability {
  turnTime: number | null;
  hasAvailability: boolean;
}
export const getWalkInAvailability = async ({
  floorPlanTableId,
  guestCount,
  restaurantId,
}: {
  floorPlanTableId: string;
  guestCount: number;
  restaurantId: string;
}) =>
  api.get<WalkInAvailability>(
    `/restaurants/${restaurantId}/floor-plan-tables/${floorPlanTableId}/walk-in-availability?guestCount=${guestCount}`,
  );
